import React, { useEffect, useRef, useState } from "react";

interface SelectMenuProps {
  options: {
    value: string;
    label: string;
    other?: string;
    disabled?: boolean;
  }[];
  onChange: (value: string) => void;
  getRowData?: (data: any) => void;
  placeholder?: string;
  className?: string;
  defaultValue?: string;
  value?: string;
  disabled?: boolean;
  height?: string;
  error?: boolean;
  helpertext?: string;
  positionOptions?: string;
}

const SelectMenu: React.FC<SelectMenuProps> = ({
  options,
  onChange,
  placeholder = "Select an option",
  className = "",
  defaultValue,
  value,
  getRowData,
  disabled = false,
  height = "auto",
  error,
  helpertext,
  positionOptions = "absolute",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(
    value || defaultValue || ""
  );
  const parentRef = useRef<HTMLButtonElement>(null);
  const childRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function updateChildWidth() {
      const parent = parentRef.current;
      const child = childRef.current;
      if (isOpen && parent && child) {
        const parentWidth = parent.offsetWidth;
        const parentLeft = parent.getBoundingClientRect().left;
        const rect = parentRef.current.getBoundingClientRect();

        child.style.width = `${parentWidth}px`;
        if (positionOptions === "fixed") {
          child.style.left = `${parentLeft}px`;
          childRef.current.style.top = `${rect.bottom}px`;
          childRef.current.style.left = `${rect.left}px`;
          childRef.current.style.width = `${rect.width}px`;
        }
      }
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (
        parentRef.current &&
        !parentRef.current.contains(event.target as Node) &&
        childRef.current &&
        !childRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    window.addEventListener("resize", updateChildWidth);
    document.addEventListener("mousedown", handleClickOutside);
    updateChildWidth();
    return () => {
      window.removeEventListener("resize", updateChildWidth);
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSelect = (value: string) => {
    setSelectedValue(value);
    onChange(value);
    setIsOpen(false);
  };

  useEffect(() => {
    if (value && (options?.length === 0 || options?.length > 1)) {
      setSelectedValue(value || "");
    } else if (options?.length === 1) {
      const singleOption = options[0];
      setSelectedValue(singleOption?.value);
      onChange(singleOption?.value);
      getRowData && getRowData(singleOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, value]);

  const selectedOption = options?.find((opt) => opt?.value === selectedValue);

  return (
    <div
      className={`relative ${
        disabled ? "cursor-not-allowed opacity-50" : ""
      } ${className}`}
    >
      <button
        type="button"
        className="w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        ref={parentRef}
      >
        {selectedOption ? selectedOption.label : placeholder}
        <span className="absolute inset-y-0 right-0 flex items-center pr-2">
          <svg
            className={`w-5 h-5 text-gray-400 transform ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </span>
      </button>
      {error && (
        <span className="text-[#d32f2f] text-[0.75rem]">{helpertext}</span>
      )}

      {isOpen && (
        <div
          className={`${positionOptions} z-[9999] mt-1 bg-white border border-gray-300 rounded-md shadow-lg overflow-y-auto`}
          style={{ maxHeight: height }}
          ref={childRef}
        >
          {options?.map((option, index) => (
            <div
              key={`${index}`}
              className={`px-4 py-2 cursor-pointer hover:bg-gray-100 text-[12px] ${
                selectedValue === option?.value
                  ? "bg-blue-50 text-blue-600"
                  : ""
              } ${option?.disabled ? "cursor-not-allowed opacity-50" : ""}`}
              onClick={() => {
                if (option?.disabled) return;
                getRowData && getRowData(option);
                handleSelect(option?.value);
              }}
            >
              {option?.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelectMenu;
